import React from 'react';
import './Footer.css';
import logo from '../assets/logo.jpg';
import LottiePlayer from './LottiePlayer';
import simpleAiPulse from '../assets/Lottie/simple ai pulse.json';

const ConfigIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 21V14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M4 10V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 21V12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 8V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 21V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M20 12V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M1 14H7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M9 8H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M17 16H23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DualAnalysisIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 7H3C1.89543 7 1 7.89543 1 9V19C1 20.1046 1.89543 21 3 21H21C22.1046 21 23 20.1046 23 19V9C23 7.89543 22.1046 7 21 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M16 21V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 21V7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M3 7L12 3L21 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const MapIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 10C21 17 12 23 12 23C12 23 3 17 3 10C3 7.61305 3.94821 5.32387 5.63604 3.63604C7.32387 1.94821 9.61305 1 12 1C14.3869 1 16.6761 1.94821 18.364 3.63604C20.0518 5.32387 21 7.61305 21 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

interface FooterProps {
    onOpenDoc: (docName: string, title: string) => void;
}

const Footer: React.FC<FooterProps> = ({ onOpenDoc }) => {
    const [activeTooltip, setActiveTooltip] = React.useState<number | null>(null);

    const handleFeatureClick = (index: number) => {
        // Toggle tooltip on mobile
        setActiveTooltip(activeTooltip === index ? null : index);
    };

    const features = [
        {
            icon: <ConfigIcon />,
            title: "As simple or complex as you want it to be",
            description: "Fully configurable: Set it and forget it or follow along to make the best choice for every image"
        },
        {
            icon: <DualAnalysisIcon />,
            title: "Dual analysis",
            description: "File and Folders Scanning + AI image recognition"
        },
        {
            icon: <MapIcon />,
            title: "World Map",
            description: "View your images on a world map and move them around as needed"
        }
    ];

    return (
        <footer className="app-footer">
            <div className="footer-main-content">
                {/* Features Section (left side on desktop) */}
                <div className="footer-features-section">
                    <div className="footer-feature-list">
                        {features.map((feature, index) => (
                            <div
                                key={index}
                                className={`footer-feature-card ${activeTooltip === index ? 'mobile-tooltip-active' : ''}`}
                                onClick={() => handleFeatureClick(index)}
                            >
                                <div className="footer-feature-icon-wrapper">
                                    {feature.icon}
                                </div>
                                <h3>{feature.title}</h3>
                                <div className="footer-feature-tooltip">
                                    {feature.description}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer Links Section (center on desktop) */}
                <div className="footer-content">
                    <div className="footer-brand">
                        <img src={logo} alt="Smart Geotag Logo" className="footer-logo" />
                        <p className="footer-tagline">Smart Geotagging for everyone.</p>
                    </div>

                    <div className="footer-links">
                        <div className="footer-column">
                            <h4>Key Features</h4>
                            <ul>
                                <li><a href="/guide/what-is-smartgeotag" onClick={(e) => { e.preventDefault(); onOpenDoc('what-is-smartgeotag', 'What is Smartgeotag.ai'); }}>What is Smartgeotag.ai</a></li>
                                <li><a href="/guide/why-subscribe" onClick={(e) => { e.preventDefault(); onOpenDoc('why-subscribe', 'Why Subscribe?'); }}>Why Subscribe?</a></li>
                                <li><a href="/guide/how-to" onClick={(e) => { e.preventDefault(); onOpenDoc('how-to', 'How to'); }}>How to</a></li>
                                <li><a href="/guide/tips-and-tricks" onClick={(e) => { e.preventDefault(); onOpenDoc('tips-and-tricks', 'Tips & Tricks'); }}>Tips & Tricks</a></li>
                                <li><a href="/guide/faq" onClick={(e) => { e.preventDefault(); onOpenDoc('faq', 'FAQ'); }}>FAQ</a></li>
                            </ul>
                        </div>

                        <div className="footer-column">
                            <h4>Resources</h4>
                            <ul>
                                <li><a href="/guide/credits" onClick={(e) => { e.preventDefault(); onOpenDoc('credits', 'Credits'); }}>Credits</a></li>
                                <li><a href="/guide/support" onClick={(e) => { e.preventDefault(); onOpenDoc('support', 'Support'); }}>Support</a></li>
                            </ul>
                        </div>

                        <div className="footer-column">
                            <h4>Legal</h4>
                            <ul>
                                <li><a href="/guide/privacy-policy" onClick={(e) => { e.preventDefault(); onOpenDoc('privacy-policy', 'Privacy Policy'); }}>Privacy Policy</a></li>
                                <li><a href="/guide/terms-of-service" onClick={(e) => { e.preventDefault(); onOpenDoc('terms-of-service', 'Terms of Service'); }}>Terms of Service</a></li>
                                <li><a href="/guide/gdpr-compliant" onClick={(e) => { e.preventDefault(); onOpenDoc('gdpr-compliant', 'GDPR Compliant'); }}>GDPR Compliant</a></li>
                            </ul>
                        </div>
                    </div>
                </div>

                {/* Lottie Section (right side on desktop) */}
                <div className="footer-lottie-wrapper">
                    <div className="footer-lottie-tooltip">
                        <LottiePlayer
                            animationData={simpleAiPulse}
                            loop={true}
                            autoplay={true}
                            className="footer-lottie"
                        />
                        <span className="footer-lottie-tooltip__text">Powered by AI</span>
                    </div>
                </div>
            </div>

            <div className="footer-bottom">
                <p>&copy; {new Date().getFullYear()} Smartgeotag.ai. All rights reserved.</p>
            </div>
        </footer>
    );
};

export default Footer;
